/**
 * OrdersTable Section Component
 *
 * Composes DataTableProvider + DataTableContent with the sales order columns.
 * Handles server-side pagination and row click navigation.
 */

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { DataTableProvider } from '../../../DataTable/DataTableProvider';
import { DataTableContent } from '../../../DataTable/DataTableContent';
import { SALES_ORDER_TABLE_MIN_WIDTH } from '../../../DataTable/columnWidths';
import { DEFAULT_PAGE_SIZE } from '../../../DataTable/constants';
import { getSalesOrderColumns } from '../../../SalesOrderTable/columns';
import { OrderSkeletonCell } from '../../../SalesOrderTable/OrderSkeletonCell';
import type { OrdersTableProps } from '../types';

export function OrdersTable({
  orders,
  isLoading = false,
  isFetching = false,
  onRowClick,
  onOrderIdClick,
  currentUser,
  className,
}: OrdersTableProps) {
  const columns = useMemo(() => getSalesOrderColumns({ onOrderIdClick, currentUser }), [onOrderIdClick, currentUser]);

  return (
    <div className={cn('flex flex-col', className)}>
      <DataTableProvider
        columns={columns}
        data={orders}
        isLoading={isLoading}
        isFetching={isFetching}
      >
        <DataTableContent
          onRowClick={onRowClick}
          emptyTitle="No orders found"
          skeletonRows={DEFAULT_PAGE_SIZE}
          renderSkeletonCell={(column) => <OrderSkeletonCell columnId={column.id} />}
          minTableWidth={SALES_ORDER_TABLE_MIN_WIDTH}
        />
      </DataTableProvider>
    </div>
  );
}
